/**
 * The height half of the embedded-host protocol, shared by every emitter that sizes a host iframe.
 *
 * Why this is not inlined at the two call sites: `useEmbeddedHostResize` (SIU, app-wide) and
 * `ClaimSearchDetailCard` (FSC, panel-scoped) post the identical `{ type: 'resize', height }`
 * message, and the two rules that make it correct — which metric to report and never to add a
 * buffer to it — are easy to get subtly wrong and were previously maintained twice.
 */
export interface EmbeddedHeightReporter {
  /** Posts the current document height, unless it is the one already posted. */
  postHeight: () => void;
}

/**
 * Why: report the tallest of the standard height metrics. `body.scrollHeight` alone under-reports —
 * it omits trailing margins and, in some flex/grid layouts, does not stretch to the full content —
 * leaving the iframe too short; `documentElement.scrollHeight` reflects the true document height.
 *
 * Edge case: do NOT add a buffer to this value. `documentElement.scrollHeight` is always at least
 * the viewport (iframe) height, so any constant added to it compounds every time the host applies
 * the new height and the frame grows without bound. The metric is an integer that already contains
 * all content, so no fudge factor is needed to avoid a residual scrollbar.
 *
 * That same floor means this metric can grow the iframe but never shrink it, which is right for a
 * host that only ever needs the frame to fit (SIU) and wrong for one that must shrink it back.
 */
export const measureDocumentHeight = (): number => {
  return Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );
};

/**
 * The body's own height, which is not floored at the viewport, so a posted value can be smaller
 * than the iframe it is sent to. This is what the FSC claim-search panel has always posted.
 */
export const measureBodyHeight = (): number => {
  return document.body.scrollHeight;
};

export const createEmbeddedHeightReporter = (
  measure: () => number = measureDocumentHeight
): EmbeddedHeightReporter => {
  // Why: the host sizes the iframe to the height we post, which changes our own viewport and fires
  // the observer again with the identical value. Deduping on the last posted height stops that echo
  // once it settles; deduping on the overhang below stops the echo of viewport-relative content.
  let lastPostedHeight = -1;
  // How far the posted height stood above the viewport it was measured in; null until first post.
  let lastPostedOverhang: number | null = null;

  const postHeight = () => {
    const height = measure();
    const overhang = height - window.innerHeight;

    if (height === lastPostedHeight) {
      return;
    }

    // Edge case: content sized in viewport units (an element at `100vh`, a `min-height: 100vh`)
    // grows by exactly what the host just added to the iframe, so the height is new every time and
    // the dedupe above never settles — the frame grew ~60 times a second without bound. A height
    // whose overhang over the viewport is unchanged grew only because the frame did, so posting it
    // would just restart the loop. Real growth or shrinkage changes the overhang and still posts.
    if (overhang === lastPostedOverhang) {
      return;
    }
    lastPostedHeight = height;
    lastPostedOverhang = overhang;

    // Security: '*' target origin is acceptable — the payload is a layout metric only, never claim
    // or party data. The host origin is not in runtime config, so it cannot be allowlisted here.
    window.parent.postMessage({ type: 'resize', height }, '*');
  };

  return { postHeight };
};
